import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi, errMsg, notifyRefresh } from "@/lib/api";
import { brl, todayISO } from "@/lib/constants";

export const PlanDialog = ({ open, onOpenChange, patientId }) => {
  const api = useApi();
  const [f, setF] = useState({ description: "", total: "", installments: "1", first_due_date: todayISO(), category: "tratamento" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setF({ description: "", total: "", installments: "1", first_due_date: todayISO(), category: "tratamento" });
  }, [open]);

  const n = Number(f.installments) || 1;
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/patients/${patientId}/plans`, { ...f, total: Number(f.total), installments: n });
      toast.success("Orçamento lançado");
      notifyRefresh();
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="plan-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Novo orçamento / plano de pagamento</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4">
          <div className="grid gap-1.5">
            <Label>Descrição do tratamento</Label>
            <Input required minLength={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Ex.: Tratamento de canal 36" data-testid="plan-description-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Valor total (R$)</Label>
              <Input type="number" step="0.01" min="0.01" required value={f.total} onChange={(e) => setF({ ...f, total: e.target.value })} data-testid="plan-total-input" />
            </div>
            <div className="grid gap-1.5">
              <Label>Parcelas</Label>
              <Select value={f.installments} onValueChange={(v) => setF({ ...f, installments: v })}>
                <SelectTrigger data-testid="plan-installments-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }, (_, i) => i + 1).map((i) => <SelectItem key={i} value={String(i)}>{i}x</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>1º vencimento</Label>
              <Input type="date" required value={f.first_due_date} onChange={(e) => setF({ ...f, first_due_date: e.target.value })} data-testid="plan-first-due-input" />
            </div>
            <div className="grid gap-1.5">
              <Label>Tipo</Label>
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                <SelectTrigger data-testid="plan-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tratamento">Tratamento</SelectItem>
                  <SelectItem value="ortodontia">Ortodontia (instalação)</SelectItem>
                  <SelectItem value="retorno">Retorno</SelectItem>
                  <SelectItem value="outro">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {Number(f.total) > 0 && (
            <p className="rounded-xl bg-sky-50 p-3 text-sm text-sky-800" data-testid="plan-preview">
              {n}x de <b>{brl(Number(f.total) / n)}</b> — vencimentos mensais
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} className="bg-sky-600 hover:bg-sky-700" data-testid="plan-save-btn">
              {saving ? "Salvando..." : "Lançar orçamento"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
